import React, { useState, useCallback } from 'react';
import { JobSourceEditorProps, FormErrors } from './types';
import BasicInfoSection from './sections/BasicInfoSection';
import CompanyInfoSection from './sections/CompanyInfoSection';
import AgentInfoSection from './sections/AgentInfoSection';
import RelationsSection from './sections/RelationsSection';
import MediaSection from './sections/MediaSection';
import WriterArticleSection from './sections/WriterArticleSection';

type TabType = 'basic' | 'company' | 'agent' | 'relations' | 'media' | 'writer';

const JobSourceEditor: React.FC<JobSourceEditorProps> = ({
  jobSource: initialJobSource,
  contractTypes,
  prefectures,
  occupations,
  job_source_features,
  updateUrl,
  csrfToken,
  backUrl,
}) => {

  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState(initialJobSource);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const formDataToSend = new FormData();
      
      // ファイルアップロード
      if (logoFile) {
        formDataToSend.append('job_source[logo]', logoFile);
      }
      if (thumbnailFile) {
        formDataToSend.append('job_source[thumbnail]', thumbnailFile);
      }
      
      // 基本情報
      Object.keys(formData).forEach((key) => {
        const value = formData[key as keyof typeof formData];
        if (value === null || value === undefined || key === 'logo_url' || key === 'thumbnail_url') return;
        
        // 優先順位のハッシュは特別に処理
        if ((key === 'skill_priorities' || key === 'occupation_priorities' || key === 'industry_priorities') && typeof value === 'object' && value !== null) {
          const priorities = value as Record<number, number>;
          Object.keys(priorities).forEach((id) => {
            formDataToSend.append(`job_source[${key}][${id}]`, String(priorities[Number(id)]));
          });
          return;
        }
        
        if (Array.isArray(value)) {
          if (value.length === 0) {
            formDataToSend.append(`job_source[${key}][]`, '');
          } else {
            value.forEach((item, index) => {
              if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
                // 構造化された配列（benefits, merits等）
                Object.keys(item).forEach((subKey) => {
                  formDataToSend.append(`job_source[${key}][${index}][${subKey}]`, String(item[subKey as keyof typeof item]));
                });
              } else {
                formDataToSend.append(`job_source[${key}][]`, String(item));
              }
            });
          }
        } else if (typeof value === 'object' && value !== null && !(value instanceof File)) {
          // オブジェクトの場合はJSON文字列として送信
          formDataToSend.append(`job_source[${key}]`, JSON.stringify(value));
        } else if (value instanceof File) {
          formDataToSend.append(`job_source[${key}]`, value);
        } else {
          formDataToSend.append(`job_source[${key}]`, String(value));
        }
      });

      const response = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: formDataToSend,
      });

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        
        if (response.ok && result.success) {
          // 成功時：リダイレクトせず、成功メッセージを表示
          setSuccessMessage(result.message || '更新が完了しました');
          setErrors({});
          
          // ロゴとサムネイルのURLを更新
          if (result.job_source) {
            setFormData((prev) => ({
              ...prev,
              logo_url: result.job_source.logo_url || prev.logo_url,
              thumbnail_url: result.job_source.thumbnail_url || prev.thumbnail_url,
            }));
          }
          
          // 成功メッセージを3秒後に自動で非表示
          setTimeout(() => {
            setSuccessMessage(null);
          }, 3000);
        } else {
          // エラー時：エラーメッセージを表示
          setErrors(result.errors || {});
          setSuccessMessage(null);
        }
      } else {
        // JSON以外のレスポンス（HTMLなど）の場合は従来通りリダイレクト
        if (response.ok || response.redirected) {
          window.location.href = response.redirected ? response.url : backUrl;
        } else {
          setErrors({ base: ['保存中にエラーが発生しました'] });
        }
      }
    } catch (error) {
      console.error('Error updating job source:', error);
      setErrors({ base: ['保存中にエラーが発生しました'] });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, logoFile, thumbnailFile, updateUrl, csrfToken, backUrl]);

  const tabs: Array<{ id: TabType; label: string }> = React.useMemo(() => [
    { id: 'basic' as TabType, label: '基本情報' },
    { id: 'company' as TabType, label: '会社情報' },
    { id: 'agent' as TabType, label: 'エージェント情報' },
    { id: 'relations' as TabType, label: 'リレーション' },
    { id: 'media' as TabType, label: 'メディア' },
    { id: 'writer' as TabType, label: 'ライター解説' },
  ], []);

  return (
    <div className="container-fluid">
      <header className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h3 mb-2">求人ソース編集</h1>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <a href={backUrl} className="btn btn-outline-secondary">
            <i className="bi bi-list-ul me-2"></i>
            一覧に戻る
          </a>
        </div>
      </header>

      {successMessage && (
        <div className="alert alert-success border border-success-subtle bg-success-subtle text-success-emphasis shadow-sm mb-4" role="alert">
          <div className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-2"></i>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger border border-danger-subtle bg-danger-subtle text-danger-emphasis shadow-sm mb-4" role="alert">
          <h2 className="h6 fw-semibold mb-2">エラーが発生しました</h2>
          <ul className="mb-0 ps-3">
            {Object.entries(errors).map(([field, fieldErrors]) => (
              <li key={field} className="mb-1">
                {field}: {fieldErrors.join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <ul className="nav nav-tabs card-header-tabs" role="tablist">
              {tabs.map((tab) => (
                <li key={tab.id} className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-body">
            <div className="tab-content">
              {activeTab === 'basic' && (
                <BasicInfoSection
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
              )}
              {activeTab === 'company' && (
                <CompanyInfoSection
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
              )}
              {activeTab === 'agent' && (
                <AgentInfoSection
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
              )}
              {activeTab === 'relations' && (
                <RelationsSection
                  formData={formData}
                  setFormData={setFormData}
                  contractTypes={contractTypes}
                  prefectures={prefectures}
                  occupations={occupations}
                  job_source_features={job_source_features || []}
                  errors={errors}
                />
              )}
              {activeTab === 'media' && (
                <MediaSection
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  onLogoFileChange={setLogoFile}
                  onThumbnailFileChange={setThumbnailFile}
                />
              )}
              {activeTab === 'writer' && (
                <WriterArticleSection
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
              )}
            </div>
          </div>
          <div className="card-footer bg-white">
            <div className="d-flex justify-content-end gap-2">
              <a href={backUrl} className="btn btn-secondary">
                キャンセル
              </a>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
                style={{ backgroundColor: '#00bb94', borderColor: '#00bb94' }}
              >
                {isSubmitting ? '保存中...' : '更新'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default JobSourceEditor;

